// module.manager.js - централизованный регистр модулей
import { useStore } from '../store/core.store.js';
import { i18nManager } from './i18n.manager.js';

export class ModuleManager {
  constructor() {
    this.modules = new Map();
    this.loaders = new Map();
    this.loadingPromises = new Map(); // Для предотвращения дублирования загрузки
    // Идущие активации: store запроса → Map(имя модуля → промис активации).
    // Ключ — объект стора, поэтому SSR-запросы не видят промисы друг друга и
    // чистить записи руками не нужно (стор запроса умирает — умирает и запись).
    this.activations = new WeakMap();
    // Имя модуля, чей register() выполняется прямо сейчас. Читает addRoutes:
    // он проставляет это имя в meta.modules каждой добавленной записи маршрута,
    // и по нему activateRoute понимает, какой модуль нужен маршруту.
    this.current = null;
  }

  // Регистрация модуля с ленивой загрузкой
  register(name, config) {
    this.loaders.set(name, {
      loader: config.loader,
      dependencies: config.dependencies || [],
      contexts: config.contexts,
      // Произвольный модуль-специфичный конфиг — passthrough в initialize().
      // Используется чтобы прокидывать опции модуля (например analytics.adapters)
      // без расширения whitelist'а ядра. Имя `settings` чтобы не конфликтовать
      // с 4-м аргументом initialize() который во многих модулях называется `options`.
      settings: config.settings || {},
    });
  }

  // Загрузка модуля: только импорт с дедупликацией и кэшем.
  // initialize() здесь не зовётся — это делает activate().
  async load(name) {
    if (this.modules.has(name)) {
      return this.modules.get(name);
    }

    if (this.loadingPromises.has(name)) {
      return this.loadingPromises.get(name);
    }

    const config = this.loaders.get(name);
    if (!config) {
      throw new Error(`Module ${name} not registered`);
    }

    const loadPromise = config.loader().then(module => {
      this.modules.set(name, module);
      return module;
    });
    this.loadingPromises.set(name, loadPromise);

    try {
      const module = await loadPromise;
      this.loadingPromises.delete(name);
      return module;
    } catch (error) {
      this.loadingPromises.delete(name);
      throw error;
    }
  }

  // Старт приложения: импортируем ВСЕ модули и даём каждому объявить свои
  // маршруты (register → addRoutes). Импорт кэшируется движком, register дёшев,
  // поэтому это выполняется на каждый createApp (каждый SSR-запрос и один раз
  // на клиенте). Роутер после этого знает про все маршруты всех модулей.
  async registerAll(context) {
    const { app, store, router, config } = context;

    for (const [name, loaderConfig] of this.loaders) {
      const mod = await this.load(name);
      if (!mod.register) continue;

      this.current = name;
      try {
        mod.register(app, store, router, { ...config, ...loaderConfig });
      } finally {
        this.current = null;
      }
    }
  }

  // Локаль модуля живёт отдельно от активации: язык маршрута известен только
  // после роут-гарда setLocale, а модули корневой записи маршрута активируются
  // раньше навигации.
  // Грузим ровно один язык — остальные это чужой вес в бандле и в data-state.
  async loadLocale(name, lang, i18n) {
    if (!lang || i18nManager.has(name, lang)) {
      return;
    }

    const mod = await this.load(name);
    const locales = mod.locales || mod.default?.locales;
    if (!locales?.[lang]) {
      return;
    }

    i18nManager.merge(name, lang, (await locales[lang]()).default, i18n);
  }

  // Активация модуля: зависимости → initialize() → локаль текущего языка.
  // Признак «активирован в этом запросе» живёт на сторе (per-request на SSR
  // через AsyncLocalStorage, синглтон на клиенте), поэтому параллельные SSR-запросы
  // не считают чужую активацию своей.
  activate(name, context) {
    const { app, store, router, config } = context;

    let pending = this.activations.get(store);
    if (!pending) {
      pending = new Map();
      this.activations.set(store, pending);
    }

    // Активация уже идёт или закончилась — отдаём тот же промис. Возврат до
    // конца initialize отдал бы вызывающему стор, которого ещё нет.
    if (pending.has(name)) {
      return pending.get(name);
    }

    const loaderConfig = this.loaders.get(name);
    if (!loaderConfig) {
      return Promise.reject(new Error(`Module ${name} not registered`));
    }

    store.activated.push(name);

    const activation = (async () => {
      for (const dep of loaderConfig.dependencies) {
        await this.activate(dep, context);
      }

      const mod = await this.load(name);

      const initFunc = mod.initialize || mod.default?.initialize;
      if (initFunc) {
        await initFunc(app, store, router, { ...config, ...loaderConfig });
      }

      const { i18n } = context;
      const lang = i18n.global.locale.value;
      await this.loadLocale(name, lang, i18n);
    })().catch(error => {
      // Активация не состоялась: снимаем отметки, чтобы следующий вызов пробовал заново.
      pending.delete(name);
      const index = store.activated.indexOf(name);
      if (index !== -1) store.activated.splice(index, 1);
      throw error;
    });

    pending.set(name, activation);

    return activation;
  }

  // Модули записи маршрута (meta.modules, проставляет addRoutes).
  async activateRoute(route, context) {
    const names = [];

    for (const record of route.matched || []) {
      for (const name of record.meta?.modules || []) {
        if (!names.includes(name)) names.push(name);
      }
    }

    const { i18n } = context;
    const lang = i18n.global.locale.value;

    for (const name of names) {
      await this.activate(name, context);
      // Язык маршрута мог смениться после активации (модули корневой записи
      // активируются до навигации, SPA-переход на /en — после): догружаем локаль
      // этого языка.
      await this.loadLocale(name, lang, i18n);
    }
  }
}

// Создаем глобальный менеджер
export const moduleManager = new ModuleManager();

// Компонент вне маршрутов своего модуля подключает модуль по требованию тем же
// путём, что и запись маршрута: activate с контекстом запроса. Контекст берём со
// стора (core.app.js кладёт его туда), поэтому вызывать можно из любого места без
// provide. Нужен ради initialize() модуля (плагины, подписки) — стор модуля
// создаётся сам при первом обращении к его константе.
export function activateModule(name) {
  return moduleManager.activate(name, useStore().context);
}
